const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']

/** The same units, as `Intl` names them, for a size written in the displayed language. */
const intlUnits = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte', 'petabyte'] as const

/**
 * Formats a byte count into a human-readable file size string.
 *
 * The divisor is 1024 — the filesystem's convention, which is what a person reads a file's size
 * against. Given a `locale`, the number and the unit are that language's and a trailing zero is
 * dropped (`1,5 Mo`, `500 Mo`, `1.5 MB`); without one, the output is the English `1.5 MB` form.
 *
 * @param bytes - The number of bytes to format
 * @param decimals - Number of decimal places to display (default: 1)
 * @param locale - The language to write the size in
 * @returns Formatted size string with appropriate unit (e.g., "1.5 MB")
 *
 * @example
 * formatFileSize(1024) // '1.0 KB'
 * formatFileSize(1536, 2) // '1.50 KB'
 * formatFileSize(1048576) // '1.0 MB'
 * formatFileSize(0) // '0 B'
 * formatFileSize(1536, 1, 'fr') // '1,5 ko'
 */
export function formatFileSize(bytes: number, decimals = 1, locale?: string): string {
  if (bytes < 0) return 'Invalid size'

  const k = 1024
  const i = bytes === 0 ? 0 : Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(k)))
  const size = bytes / Math.pow(k, i)

  if (locale) {
    return new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: intlUnits[i],
      unitDisplay: 'short',
      maximumFractionDigits: i === 0 ? 0 : decimals,
    }).format(size)
  }
  if (bytes === 0) return '0 B'
  return `${size.toFixed(i === 0 ? 0 : decimals)} ${units[i]}`
}
